import { TrainerMemberRelationshipWithMember } from '../types/dataModel';
import { displayNameFromProfile } from '../pages/account/accountShared';

export const MEMBER_MANAGEMENT_VIEW_MODE_KEY = 'member-management-view-mode';
export const MEMBER_MANAGEMENT_STATUS_CHIPS_KEY = 'member-management-status-chips';
export type MemberManagementViewMode = 'cards' | 'table';

export type MemberStatusChipId = 'active' | 'pending' | 'archived' | 'declined';

export type MemberStatusChips = Record<MemberStatusChipId, boolean>;

export const DEFAULT_MEMBER_STATUS_CHIPS: MemberStatusChips = {
  active: true,
  pending: true,
  archived: false,
  declined: false,
};

export type MemberSortColumn = 'member' | 'createdAt';
export type MemberSortDirection = 'asc' | 'desc';

export type MemberDateFilterType = 'exact' | 'before' | 'after' | 'between';

export interface MemberDateFilterState {
  dateFilter: string;
  dateFilterType: MemberDateFilterType;
  dateFilterEnd: string;
}

export function readStoredViewMode(): MemberManagementViewMode {
  if (typeof window === 'undefined') return 'cards';
  try {
    const raw = window.localStorage.getItem(MEMBER_MANAGEMENT_VIEW_MODE_KEY);
    if (raw === 'table') return 'table';
    return 'cards';
  } catch {
    return 'cards';
  }
}

/** Restores status chip toggles (Active / Pending / Archived / Declined) after refresh. */
export function readStoredStatusChips(): MemberStatusChips {
  if (typeof window === 'undefined') return { ...DEFAULT_MEMBER_STATUS_CHIPS };
  try {
    const raw = window.localStorage.getItem(MEMBER_MANAGEMENT_STATUS_CHIPS_KEY);
    if (!raw) return { ...DEFAULT_MEMBER_STATUS_CHIPS };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: MemberStatusChips = { ...DEFAULT_MEMBER_STATUS_CHIPS };
    for (const id of Object.keys(DEFAULT_MEMBER_STATUS_CHIPS) as MemberStatusChipId[]) {
      const v = parsed[id];
      if (typeof v === 'boolean') {
        out[id] = v;
      }
    }
    return out;
  } catch {
    return { ...DEFAULT_MEMBER_STATUS_CHIPS };
  }
}

export function relationshipMatchesStatusChips(
  relationship: TrainerMemberRelationshipWithMember,
  chips: MemberStatusChips
): boolean {
  const s = relationship.status;
  if (chips.active && s === 'active') return true;
  if (chips.pending && (s === 'pending' || s === 'onboarding')) return true;
  if (chips.archived && s === 'inactive') return true;
  if (chips.declined && s === 'rejected') return true;
  return false;
}

export function matchesMemberSearch(
  relationship: TrainerMemberRelationshipWithMember,
  searchQuery: string
): boolean {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return true;
  const display = displayNameFromProfile(relationship.member).toLowerCase();
  const email = (relationship.member?.email || '').toLowerCase();
  return display.includes(q) || email.includes(q);
}

export function matchesCreatedAtDateFilter(
  relationship: TrainerMemberRelationshipWithMember,
  state: MemberDateFilterState
): boolean {
  const { dateFilter, dateFilterType, dateFilterEnd } = state;
  if (!dateFilter) return true;
  const relationshipDate = relationship.createdAt ? new Date(relationship.createdAt) : null;
  if (!relationshipDate) return false;

  const relDate = new Date(
    relationshipDate.getFullYear(),
    relationshipDate.getMonth(),
    relationshipDate.getDate()
  );

  if (dateFilterType === 'exact') {
    const filterDate = new Date(dateFilter);
    const filterDateNormalized = new Date(
      filterDate.getFullYear(),
      filterDate.getMonth(),
      filterDate.getDate()
    );
    return relDate.getTime() === filterDateNormalized.getTime();
  }
  if (dateFilterType === 'before') {
    const filterDate = new Date(dateFilter);
    const filterDateNormalized = new Date(
      filterDate.getFullYear(),
      filterDate.getMonth(),
      filterDate.getDate()
    );
    return relDate < filterDateNormalized;
  }
  if (dateFilterType === 'after') {
    const filterDate = new Date(dateFilter);
    const filterDateNormalized = new Date(
      filterDate.getFullYear(),
      filterDate.getMonth(),
      filterDate.getDate()
    );
    return relDate > filterDateNormalized;
  }
  if (dateFilterType === 'between') {
    if (!dateFilterEnd) return false;
    const filterDateStart = new Date(dateFilter);
    const filterDateStartNormalized = new Date(
      filterDateStart.getFullYear(),
      filterDateStart.getMonth(),
      filterDateStart.getDate()
    );
    const filterDateEnd = new Date(dateFilterEnd);
    const filterDateEndNormalized = new Date(
      filterDateEnd.getFullYear(),
      filterDateEnd.getMonth(),
      filterDateEnd.getDate()
    );
    return relDate >= filterDateStartNormalized && relDate <= filterDateEndNormalized;
  }
  return true;
}

export function filterRelationships(
  members: TrainerMemberRelationshipWithMember[],
  chips: MemberStatusChips,
  searchQuery: string,
  dateState: MemberDateFilterState
): TrainerMemberRelationshipWithMember[] {
  return members.filter(
    (r) =>
      relationshipMatchesStatusChips(r, chips) &&
      matchesMemberSearch(r, searchQuery) &&
      matchesCreatedAtDateFilter(r, dateState)
  );
}

/** Primary list order when multiple statuses appear: Active → Pending/Onboarding → Archived → Declined. */
export function memberStatusSortRank(status: TrainerMemberRelationshipWithMember['status']): number {
  if (status === 'active') return 0;
  if (status === 'pending' || status === 'onboarding') return 1;
  if (status === 'inactive') return 2;
  if (status === 'rejected') return 3;
  return 4;
}

export function sortRelationships(
  filtered: TrainerMemberRelationshipWithMember[],
  sortColumn: MemberSortColumn,
  sortDirection: MemberSortDirection
): TrainerMemberRelationshipWithMember[] {
  const next = [...filtered];
  next.sort((a, b) => {
    const tierA = memberStatusSortRank(a.status);
    const tierB = memberStatusSortRank(b.status);
    if (tierA !== tierB) return tierA - tierB;

    let comparison = 0;
    switch (sortColumn) {
      case 'member': {
        const nameA = displayNameFromProfile(a.member).toLowerCase();
        const nameB = displayNameFromProfile(b.member).toLowerCase();
        comparison = nameA.localeCompare(nameB);
        break;
      }
      case 'createdAt': {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        comparison = dateA - dateB;
        break;
      }
      default:
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  return next;
}

export function filterPendingEmailInvites(
  invites: { email: string; createdAt: string }[],
  searchQuery: string,
  pendingChipOn: boolean
): { email: string; createdAt: string }[] {
  if (!pendingChipOn) return [];
  const q = searchQuery.trim().toLowerCase();
  if (!q) return [...invites];
  return invites.filter((i) => i.email.toLowerCase().includes(q));
}

/** Calendar affordance matches table rules: not pending, onboarding, or declined. */
export function canOpenMemberCalendar(relationship: TrainerMemberRelationshipWithMember): boolean {
  const s = relationship.status;
  return s !== 'pending' && s !== 'onboarding' && s !== 'rejected';
}
